import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import SearchIcon from '../../../assets/search_icon.svg';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { KeyIcon, RefreshIcon, TrashCan } from '../../../icons/SvgIcons';
import {
  authorizeRepository,
  deleteRepository,
  fetchRepositories,
  fetchRepositoryBranches,
  syncRepository,
  updateRepositoryBranch
} from '../../../services/RepositoryService';
import { getAISystemOverview } from '../../../services/AISystemsService';
import {
  deleteRepositoryFailure,
  deleteRepositoryStart,
  deleteRepositorySuccess,
  fetchRepositoriesSuccess,
  isReqListStepStarted,
  setLoading,
  syncRepositoryFailure,
  syncRepositoryStart,
  syncRepositorySuccess,
  updateRepositoryBranchFailure,
  updateRepositoryBranchStart,
  updateRepositoryBranchSuccess
} from '../../../store/slices/repositorySlice';
import {
  fetchCurrentOverviewFailure,
  fetchCurrentOverviewSuccess,
  setCurrentOverviewAiSystemId
} from '../../../store/slices/aiSystemsSlice';
import {
  Repository,
  RepositoryBranch,
  RepositoryProvider,
  RepositoryStatus
} from '../../../types/Repository.types';
import { TableHeader } from '../../tables/AISystemsTable/components/TableHeader/TableHeader';
import DropdownPortal from '../../ui/DropdownPortal/DropdownPortal';
import { EmptyState } from '../../ui/EmptyState/EmptyState';
import Loader from '../../ui/Loader/Loader';
import ModalDeleteConfirmation from '../../ui/Modal/ModalDeleteConfirmation';
import { hideToast } from '../../../store/slices/toastSlice';
import { fetchRequirementStepsByAISystemId } from '../../../store/slices/requirementStepsSlice';

interface RepositoryManagementTableProps {
  hasMore: boolean;
  onLoadMore: () => void;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort: (sortKey: string) => void;
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
  canManageRepositories: boolean;
}

export function RepositoryManagementTable({
  hasMore,
  onLoadMore,
  sortBy,
  sortOrder,
  onSort,
  onError,
  onSuccess,
  canManageRepositories
}: RepositoryManagementTableProps) {
  const dispatch = useAppDispatch();
  const { id: systemId } = useParams<{ id: string }>();
  const { repositories, loading, syncLoading } = useAppSelector(
    (state) => state.repository
  );
  const { steps } = useAppSelector((state) => state.requirementSteps);

  const observerTarget = useRef<HTMLDivElement>(null);
  const [branchesMap, setBranchesMap] = useState<
    Record<string, RepositoryBranch[]>
  >({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [repositoryToDelete, setRepositoryToDelete] =
    useState<Repository | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (systemId) {
      setLoading(true);
      dispatch(fetchRequirementStepsByAISystemId(systemId)).finally(() => {
        setLoading(false);
      });
    }
  }, [systemId]);

  const isAnnexIVStarted = isReqListStepStarted(steps, 'ANNEX_IV');
  const isFriaStarted = isReqListStepStarted(steps, 'FRIA_REQUIRED');
  const disableButton = isAnnexIVStarted || isFriaStarted;
  const disableRepositoryMutation = disableButton || !canManageRepositories;

  // Fetch branches for all repositories
  useEffect(() => {
    const loadBranches = async () => {
      const newBranchesMap: Record<string, RepositoryBranch[]> = {};
      const reposToFetch = repositories.filter(
        (repo) => !branchesMap[repo.id] && repo.status !== 'PENDING_AUTH'
      );

      if (reposToFetch.length === 0) return;

      await Promise.all(
        reposToFetch.map(async (repo) => {
          try {
            const branches = await fetchRepositoryBranches(repo.id);
            newBranchesMap[repo.id] = branches;
          } catch (error) {
            // Silently handle error - just set empty branches array
            newBranchesMap[repo.id] = [];

            if (activeRepositories.length === 0) {
              dispatch(hideToast());
            }
          }
        })
      );

      if (Object.keys(newBranchesMap).length > 0) {
        setBranchesMap((prev) => ({ ...prev, ...newBranchesMap }));
      }
    };

    if (repositories.length > 0) {
      loadBranches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repositories]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          onLoadMore?.();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, onLoadMore]);

  const activeRepositories = repositories.filter(
    (repo) => repo.status !== 'PENDING_AUTH'
  );
  const pendingAuthRepositories = repositories.filter(
    (repo) => repo.status === 'PENDING_AUTH'
  );

  const tdClasses =
    'flex items-center px-6 py-3 text-sm text-gray-550 text-left text-nowrap';

  const handleSync = async (repositoryId: string) => {
    if (!canManageRepositories) {
      return;
    }
    console.log('handleSync called for repository:', repositoryId);
    dispatch(syncRepositoryStart(repositoryId));
    try {
      console.log('Calling syncRepository API...');
      const response = await syncRepository(repositoryId, false);
      console.log('Sync response:', response);
      dispatch(syncRepositorySuccess(repositoryId));

      // Refetch repositories to get updated syncInProgress flag from server
      if (systemId) {
        console.log('Refetching repositories to get sync status...');
        const reposResponse = await fetchRepositories({ aiSystemId: systemId });
        dispatch(fetchRepositoriesSuccess(reposResponse));
      }

      onSuccess?.(response.message || 'Sync started successfully');
    } catch (error: any) {
      console.error('Sync error:', error);
      dispatch(syncRepositoryFailure({ repositoryId, error: error.message }));
      onError?.(error.message || 'Failed to sync repository');
    }
  };

  const handleDeleteClick = (repository: Repository) => {
    if (!canManageRepositories) {
      return;
    }
    setRepositoryToDelete(repository);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!repositoryToDelete) return;

    setIsDeleting(true);
    dispatch(deleteRepositoryStart());
    try {
      await deleteRepository(repositoryToDelete.id);
      dispatch(deleteRepositorySuccess(repositoryToDelete.id));
      setShowDeleteModal(false);
      setRepositoryToDelete(null);

      if (systemId) {
        try {
          const overview = await getAISystemOverview(systemId);
          dispatch(setCurrentOverviewAiSystemId(systemId));
          dispatch(fetchCurrentOverviewSuccess(overview));
        } catch (error) {
          console.error('Failed to refresh overview data:', error);
          dispatch(fetchCurrentOverviewFailure('Failed to fetch overview data'));
        }
      }
    } catch (error: any) {
      dispatch(deleteRepositoryFailure(error.message));
      onError?.(error.message || 'Failed to delete repository');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAuth = (repositoryId: string, provider: string) => {
    if (!canManageRepositories) {
      return;
    }
    authorizeRepository(repositoryId, provider);
  };

  const handleBranchChange = async (
    repositoryId: string,
    branch: string,
    currentBranch?: string
  ) => {
    if (!canManageRepositories) {
      return;
    }
    if (branch === currentBranch) return;

    dispatch(updateRepositoryBranchStart());
    try {
      await updateRepositoryBranch(repositoryId, { branch });
      dispatch(updateRepositoryBranchSuccess({ repositoryId, branch }));

      // Refetch repositories to get updated status (SYNCING) and syncInProgress flag
      if (systemId) {
        const response = await fetchRepositories({ aiSystemId: systemId });
        dispatch(fetchRepositoriesSuccess(response));
      }

      onSuccess?.(`Branch changed to "${branch}" - sync started`);
    } catch (error: any) {
      dispatch(updateRepositoryBranchFailure(error.message));
      onError?.(error.message || 'Failed to update branch');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusDisplay = (status: RepositoryStatus) => {
    const statusMap: Record<
      RepositoryStatus,
      { label: string; className: string }
    > = {
      ACTIVE: { label: 'Connected', className: 'bg-cyan-100 text-green-700' },
      ERROR: { label: 'Error', className: 'bg-rose-100 text-rose-700' },
      SYNCING: {
        label: 'Syncing...',
        className: 'bg-yellow-100 text-yellow-700'
      },
      PENDING_AUTH: {
        label: 'Pending Auth',
        className: 'bg-gray-100 text-gray-700'
      },
      INACTIVE: { label: 'Inactive', className: 'bg-gray-100 text-gray-500' }
    };

    const statusInfo = statusMap[status];

    return (
      <div
        className={`px-2.5 py-0.5 rounded-[10px] text-xs font-medium ${statusInfo.className}`}
      >
        {statusInfo.label}
      </div>
    );
  };

  const getBranchOptions = (repo: Repository) => {
    const branches = branchesMap[repo.id];
    const currentBranch = repo.defaultBranch;

    if (branches && branches.length > 0) {
      return branches
        .filter((branch) => branch.name !== currentBranch)
        .map((branch) => ({
          value: branch.name,
          label: branch.name
        }));
    }

    // Fallback to default branch if branches not loaded yet
    const fallbackBranches = new Set<string>();
    if (repo.defaultBranch && repo.defaultBranch !== currentBranch) {
      fallbackBranches.add(repo.defaultBranch);
    }
    return Array.from(fallbackBranches).map((branch) => ({
      value: branch,
      label: branch
    }));
  };

  const getProviderLabel = (provider: RepositoryProvider) => {
    const providerLabelMap: Record<RepositoryProvider, string> = {
      GITHUB: 'GitHub',
      GITLAB: 'GitLab',
      BITBUCKET: 'Bitbucket'
    };

    return providerLabelMap[provider];
  };

  if (!loading && repositories.length === 0) {
    return (
      <EmptyState
        icon={<img src={SearchIcon} alt="No data" className="w-12 h-12" />}
        title="No repositories matched your criteria."
        description="Try adjusting your search or filters."
      />
    );
  }

  return (
    <div className="w-full space-y-4">
      <div className="w-full overflow-hidden rounded-lg bg-white shadow-sm border border-solid border-gray-250v2">
        {activeRepositories.length > 0 && (
          <table className="w-full">
            <TableHeader
              type="RepositoryManagement"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={onSort}
            />
            <tbody>
              {activeRepositories.map((repo) => {
                const branchOptions = getBranchOptions(repo);
                return (
                  <tr
                    key={repo.id}
                    className="w-full flex flex-row bg-white border-t border-gray-100"
                  >
                    <td
                      className={`${tdClasses} w-1/4 text-zinc-900 font-medium`}
                    >
                      {repo.name}
                    </td>
                    <td className={`${tdClasses} w-1/6`}>
                      {getProviderLabel(repo.provider)}
                    </td>
                    <td
                      className={`${tdClasses} w-1/6 min-w-[170px] overflow-hidden`}
                    >
                      <DropdownPortal
                        onChange={(selected) => {
                          handleBranchChange(
                            repo.id,
                            selected,
                            repo.defaultBranch
                          );
                        }}
                        disabled={!canManageRepositories}
                        options={branchOptions}
                        placeholder={repo.defaultBranch}
                        className="w-full max-w-[170px]"
                        buttonClassName="!px-[10px] !py-[6px] rounded-md gap-4 !w-full !h-auto"
                        placeholderClassName="text-sm text-gray-800 truncate"
                        menuClassName="!min-w-0 !max-w-[170px] !max-h-[200px]"
                        optionClassName="!block !overflow-hidden !whitespace-nowrap !text-ellipsis"
                      />
                    </td>
                    <td className={`${tdClasses} w-1/6`}>
                      {getStatusDisplay(repo.status)}
                    </td>
                    <td className={`${tdClasses} w-1/5`}>
                      {repo.lastSynced ? formatDate(repo.lastSynced) : '--'}
                    </td>
                    <td className={`${tdClasses} w-32 justify-between`}>
                      <button
                        id={`repository-sync-button-${repo.id}`}
                        data-testid={`repository-sync-button-${repo.id}`}
                        onClick={() => handleSync(repo.id)}
                        className="pr-3 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={
                          syncLoading[repo.id] ||
                          repo.status === 'SYNCING' ||
                          !canManageRepositories
                        }
                      >
                        {RefreshIcon(true)}
                      </button>
                      <button
                        id={`repository-delete-button-${repo.id}`}
                        data-testid={`repository-delete-button-${repo.id}`}
                        onClick={() => handleDeleteClick(repo)}
                        disabled={disableRepositoryMutation}
                        className="disabled:cursor-not-allowed"
                      >
                        {TrashCan('small', disableRepositoryMutation)}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* pending auth table */}
        {pendingAuthRepositories.length > 0 && (
          <table className="w-full">
            <thead className="w-full">
              <tr className="w-full flex flex-row">
                <th className="w-1/4 flex items-center py-3 px-6 gap-2 bg-gray-40 text-xs font-medium tracking-wider text-gray-550 uppercase">
                  PENDING AUTHENTICATION
                </th>
                <th className="w-1/6 flex items-center py-3 px-6 gap-2 bg-gray-40 text-xs font-medium tracking-wider text-gray-550 uppercase">
                  PROVIDER
                </th>
                <th className="w-1/6 min-w-[170px] flex items-center py-3 px-6 gap-2 bg-gray-40 text-xs font-medium tracking-wider text-gray-550 uppercase">
                  BRANCH
                </th>
                <th className="w-1/6 flex items-center py-3 px-6 gap-2 bg-gray-40 text-xs font-medium tracking-wider text-gray-550 uppercase">
                  STATUS
                </th>
                <th className="w-1/6 flex items-center py-3 px-6 gap-2 bg-gray-40 text-xs font-medium tracking-wider text-gray-550 uppercase">
                  MODELS
                </th>
                <th className="w-1/5 flex items-center py-3 px-6 gap-2 bg-gray-40 text-xs font-medium tracking-wider text-gray-550 uppercase">
                  LAST SYNCED
                </th>
                <th className="w-32 flex items-center py-3 px-6 gap-2 bg-gray-40 text-xs font-medium tracking-wider text-gray-550 uppercase">
                  ACTIONS
                </th>
              </tr>
            </thead>
            <tbody>
              {pendingAuthRepositories.map((repo) => (
                <tr
                  key={repo.id}
                  className="w-full flex flex-row bg-white border-t border-gray-100 min-h-[58px]"
                >
                  <td
                    className={`${tdClasses} w-1/4 text-zinc-900 font-medium`}
                  >
                    {repo.name}
                  </td>
                  <td className={`${tdClasses} w-1/6`}>{repo.provider}</td>
                  <td className={`${tdClasses} w-1/6 min-w-[170px]`}>--</td>
                  <td className={`${tdClasses} w-1/6`}>--</td>
                  <td className={`${tdClasses} w-1/6`}>--</td>
                  <td className={`${tdClasses} w-1/5`}>--</td>
                  <td className={`${tdClasses} w-32 justify-between`}>
                    <button
                      id={`repository-authorize-button-${repo.id}`}
                      data-testid={`repository-authorize-button-${repo.id}`}
                      onClick={() => handleAuth(repo.id, repo.provider)}
                      className="pr-3 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={!canManageRepositories}
                    >
                      {KeyIcon()}
                    </button>
                    {!disableRepositoryMutation ? (
                      <button
                        id={`repository-delete-button-${repo.id}`}
                        data-testid={`repository-delete-button-${repo.id}`}
                        onClick={() => handleDeleteClick(repo)}
                      >
                        {TrashCan('small')}
                      </button>
                    ) : (
                      TrashCan('small', true)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {hasMore && (
        <div ref={observerTarget} className="flex justify-center py-4">
          {loading && <Loader />}
        </div>
      )}

      <ModalDeleteConfirmation
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Disconnect Repository"
        description="Are you sure you want to disconnect this repository from the application? This will only remove it from your AI system, not from GitHub."
        itemName={repositoryToDelete?.name}
        isLoading={isDeleting}
      />
    </div>
  );
}
